'use client';

/**
 * WebQAInputCard — Klyde Reception input surface.
 *
 * Chat-shaped input. User types, hits Enter (or the send button),
 * docs.ask runs through the capability store, the response lands in
 * useDocsStore. The answer card and renderer slot read from the same
 * store; this card never knows what comes back.
 *
 * Substrate-only — no static content. Reuses the existing docs.ask BFF
 * strategy + useDocsStore. Skinny web-native UI; the IPE-shaped
 * DocsExplorer (history rail, turn counter) is intentionally not reused
 * — wrong shape for a public Reception surface.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useRef, useState, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { useReceptionAskStore } from '@/lib/stores/reception-ask-store';
import type { DocsResponse } from '@/lib/api/types';
import type { ComposedAnswer } from '@/lib/api/ask-types';
import { QA_SECTION } from './web-qa-chrome';

type AskComposeArgs = {
  query: string;
  conversation_id: string | null;
  mode: 'auto' | 'full';
  knowledgeOnly?: boolean; // background 'full' pass deepens only the docs answer
};

/**
 * Reception runs the klyde_ask compositor (Step 3 — all lanes). The initial
 * call is UNFILTERED, so knowledge + capability + navigate fire, scope-clamped
 * to the buyer; the background 'full' pass is knowledge-only so it only deepens
 * the docs answer rather than re-running the agent. Returns the whole
 * ComposedAnswer; the card routes each facet to its store.
 */
async function askCompose(args: AskComposeArgs): Promise<ComposedAnswer> {
  const res = await api.getStrategyData<ComposedAnswer>('ask', {
    query: args.query,
    knowledge_mode: args.mode,
    conversation_id: args.conversation_id,
    ...(args.knowledgeOnly ? { lanes: ['knowledge'] } : {}),
  });
  if (!res.success) throw new Error(res.error || 'ask failed');
  return res.data;
}

/** The knowledge facet's payload IS a DocsResponse (null if the lane didn't fire). */
function knowledgeOf(answer: ComposedAnswer): DocsResponse | null {
  const k = answer.facets.find((f) => f.lane === 'knowledge');
  return k ? (k.payload as unknown as DocsResponse) : null;
}


export function WebQAInputCard() {
  const T = useCardTypography();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);

  // klyde_ask compositor — all lanes (Step 3). The initial pass routes the
  // knowledge facet to useDocsStore (every web_qa_* docs card unchanged) and the
  // capability / navigate facets to useReceptionAskStore (the lane cards). The
  // background 'full' pass is knowledge-only — it only deepens the docs answer.
  const docs = useMutation<ComposedAnswer, Error, AskComposeArgs>({ mutationFn: askCompose });
  const docsFull = useMutation<ComposedAnswer, Error, AskComposeArgs>({ mutationFn: askCompose });
  const setResponse = useDocsStore((s) => s.setResponse);
  const setLoading = useDocsStore((s) => s.setLoading);
  const setLlmLoading = useDocsStore((s) => s.setLlmLoading);
  const setLastQuery = useDocsStore((s) => s.setLastQuery);
  const lastQuery = useDocsStore((s) => s.lastQuery);
  const setReceptionLanes = useReceptionAskStore((s) => s.setLanes);

  // Ambient project perspective. On the reception nobody clicks a project, so
  // every project-scoped card (obeya_timeline, rfi_*, etc.) reads a null
  // projectId via useViewportSelection and falls to "Select a project". Seed
  // the store with the caller's recent project — the implicit "my project"
  // anchor — so those cards bind it. Only when nothing is already selected
  // (never overrides a real selection); the detail perspective (selectedNode)
  // is left untouched.
  const selectedProjectId = useGraphSelectionStore((s) => s.projectId);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const { data: recentProject } = useQuery({
    queryKey: ['caller_recent_project'],
    queryFn: async () => {
      const res = await api.getStrategyData<{
        project_sid?: string | null;
        name?: string | null;
        rfq_count?: number;
        po_count?: number;
      }>('caller_recent_project', {});
      return res.success ? res.data : null;
    },
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (!selectedProjectId && recentProject?.project_sid) {
      setProjectContext(recentProject.project_sid, {
        name: recentProject.name ?? 'My project',
        rag_status: 'unknown',
        rag_label: '',
        rfq_count: recentProject.rfq_count ?? 0,
        po_count: recentProject.po_count ?? 0,
      });
    }
  }, [selectedProjectId, recentProject, setProjectContext]);

  const ask = (q: string) => {
    if (!q.trim()) return;
    const text = q.trim();
    setLastQuery(text);
    setLoading(true);
    setLlmLoading(false);
    // Phase 1: fast tier, all lanes. Knowledge paints the docs cards;
    // capability / navigate paint their lane cards.
    docs.mutate(
      { query: text, conversation_id: conversationId, mode: 'auto' },
      {
        onSuccess: (answer) => {
          const k = knowledgeOf(answer);
          if (k) {
            setConversationId(k.conversation_id);
            setResponse(k);
          } else {
            setLoading(false);
          }
          setReceptionLanes({
            capability: answer.facets.find((f) => f.lane === 'capability') ?? null,
            navigate: answer.facets.find((f) => f.lane === 'navigate') ?? null,
          });
          setQuery('');
          // Phase 2: knowledge wasn't confident → deepen ONLY the docs answer
          // in the background (knowledge-only, so the agent doesn't re-run).
          if (k && k.llm_status === 'auto_running') {
            setLlmLoading(true);
            docsFull.mutate(
              { query: text, conversation_id: k.conversation_id, mode: 'full', knowledgeOnly: true },
              {
                onSuccess: (full) => {
                  const k2 = knowledgeOf(full);
                  if (k2) {
                    setConversationId(k2.conversation_id);
                    setResponse(k2);
                  }
                  setLlmLoading(false);
                },
                onError: () => setLlmLoading(false),
              },
            );
          }
        },
        onError: () => {
          setLoading(false);
          setLlmLoading(false);
        },
      },
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    ask(query);
  };

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <h2 className={cn(T.name, 'mb-1')}>Ask Klyde</h2>
        <p className={cn(T.body, 'mb-3')}>
          Anything about Klyde — what we do, how it works, what it costs.
          Ask in plain language.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What would you like to know?"
              disabled={docs.isPending}
              className={cn(
                'w-full pl-5 pr-14 py-3 rounded-full',
                'bg-card border border-border text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40',
                'placeholder:text-muted-foreground transition-all',
                'disabled:opacity-60',
              )}
            />
            <button
              type="submit"
              disabled={!query.trim() || docs.isPending}
              aria-label="Send question"
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2',
                'flex items-center justify-center w-10 h-10 rounded-full',
                'bg-primary text-primary-foreground',
                'hover:-translate-y-[calc(50%+1px)] hover:shadow-lg hover:shadow-primary/25',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-[-50%] disabled:hover:shadow-none',
                'transition-all',
              )}
            >
              {docs.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>

        {lastQuery && (
          <p className={cn(T.body, 'mt-3 text-sm')}>
            <span className="text-muted-foreground">Asked: </span>
            <span className="text-foreground">{lastQuery}</span>
          </p>
        )}

        {conversationId && (
          <button
            type="button"
            onClick={() => {
              setConversationId(null);
              useDocsStore.getState().clear();
              useReceptionAskStore.getState().clear();
              setQuery('');
              inputRef.current?.focus();
            }}
            className={cn(T.metadata, 'mt-2 hover:text-foreground transition-colors')}
          >
            Start a new conversation
          </button>
        )}
      </div>
    </section>
  );
}
